import { type ReactElement } from 'react';

import {
    faLoader,
    type IconDefinition,
    faCheckCircle,
    faXmarkCircle,
    faArrowUpRight,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { CURRENCIES } from 'src/features/payStations/types';
import {
    PAYMENT_ORDER_STATUS,
    PAYMENT_STATUS,
    RESERVATION_STATUS,
    RESERVATION_TYPE,
    type ReservationsVehicleStayList,
    type ReservationType,
} from 'src/features/reservations/types/reservationType.ts';
import { reservationMessages } from 'src/features/reservations/utils/messages.ts';
import { Badge } from 'src/registry/ui/Badge.tsx';
import { generalMessages } from 'src/utils/messages.ts';

import type { BadgeTheme } from 'src/features/memberships/types';
import type { BadgeOptions } from 'src/types';

export const GetPriceTaxAmounts = (amountValue?: number | null, currency?: CURRENCIES): string | undefined => {
    const { formatNumber } = useIntl();
    return (
        (amountValue &&
            formatNumber(amountValue / 100, {
                style: 'currency',
                currency: currency,
            })) ||
        undefined
    );
};

export const formatPrice = (price: number, fractionDigits?: number) => (price / 100).toFixed(fractionDigits || 2);

export const getDefaultReservationType = (reservationType: RESERVATION_TYPE): ReservationType => {
    return {
        id: '',
        reservation_type: reservationType,
        enabled: false,
        allowed_spaces: 0,
        price: 0,
        tax_percent: '',
        currency: CURRENCIES.BGN,
        tax_amount: 0,
        price_with_tax: 0,
        reserve_space: false,
        entry_tolerance_minutes: 0,
        exit_tolerance_minutes: 0,
        valid_from: '',
        valid_to: null,
        total_reservations_count: 0,
        active_reservations_count: 0,
        upcoming_reservations_count: 0,
        scheduled_versions_count: 0,
        total_versions_count: 0,
        name: '',
        description: '',
        version_number: 0,
    };
};

export const badgeReservationStatusOptions: BadgeOptions = {
    [RESERVATION_STATUS.WAITING_PAYMENT]: {
        theme: 'orange',
        icon: faLoader,
        label: reservationMessages.waitingPayment,
    },
    [RESERVATION_STATUS.FAILED_PAYMENT]: {
        theme: 'red',
        icon: faXmarkCircle,
        label: reservationMessages.failedPayment,
    },
    [RESERVATION_STATUS.UPCOMING]: {
        theme: 'blue',
        icon: faCheckCircle,
        label: reservationMessages.upcoming,
    },
    [RESERVATION_STATUS.ACTIVE]: {
        theme: 'green',
        icon: faCheckCircle,
        label: reservationMessages.active,
    },
    [RESERVATION_STATUS.PAST]: {
        theme: 'gray',
        icon: faCheckCircle,
        label: reservationMessages.past,
    },
    [RESERVATION_STATUS.CANCELLED]: {
        theme: 'red',
        icon: faXmarkCircle,
        label: reservationMessages.cancelled,
    },
};

export const badgeReservationTypeOptions: BadgeOptions = {
    daily: {
        theme: 'white',
        icon: faCircle,
        color: {
            iconColor: 'green',
        },
        iconSize: '2xs',
        label: reservationMessages.daily,
    },
    hourly: {
        theme: 'white',
        icon: faCircle,
        color: {
            iconColor: 'blue',
        },
        iconSize: '2xs',
        label: reservationMessages.hourly,
    },
};

export const badgeReservedSpot: BadgeOptions = {
    true: {
        theme: 'white',
        icon: faCircle,
        color: {
            iconColor: 'green',
        },
        iconSize: '2xs',
        label: generalMessages.yes,
    },
    false: {
        theme: 'white',
        icon: faCircle,
        color: {
            iconColor: 'red',
        },
        iconSize: '2xs',
        label: generalMessages.no,
    },
};

export const disableActionButton = (version: ReservationType) => {
    const today = new Date();

    if (version.total_reservations_count > 0) return true;

    if (version.valid_to && new Date(version.valid_to) < today) return true;

    return false;
};

export const ReservationStatusBadge = ({
    vehiclesReservationData,
}: {
    vehiclesReservationData?: ReservationsVehicleStayList;
}) => {
    const { formatMessage } = useIntl();
    if (!vehiclesReservationData) return '-';

    const badgeColor: Record<PAYMENT_STATUS | string, BadgeTheme> = {
        [RESERVATION_STATUS.WAITING_PAYMENT]: 'orange',
        [RESERVATION_STATUS.FAILED_PAYMENT]: 'red',
        [RESERVATION_STATUS.UPCOMING]: 'blue',
        [RESERVATION_STATUS.ACTIVE]: 'green',
        [RESERVATION_STATUS.PAST]: 'gray',
        [RESERVATION_STATUS.CANCELLED]: 'red',
    };

    const badgeIcon: Record<PAYMENT_STATUS | string, IconDefinition> = {
        [RESERVATION_STATUS.WAITING_PAYMENT]: faLoader,
        [RESERVATION_STATUS.FAILED_PAYMENT]: faXmarkCircle,
        [RESERVATION_STATUS.UPCOMING]: faCheckCircle,
        [RESERVATION_STATUS.ACTIVE]: faCheckCircle,
        [RESERVATION_STATUS.PAST]: faCheckCircle,
        [RESERVATION_STATUS.CANCELLED]: faXmarkCircle,
    };

    const badgeText: Record<PAYMENT_STATUS | string, string> = {
        [RESERVATION_STATUS.WAITING_PAYMENT]: formatMessage(reservationMessages.waitingPayment),
        [RESERVATION_STATUS.FAILED_PAYMENT]: formatMessage(reservationMessages.failedPayment),
        [RESERVATION_STATUS.UPCOMING]: formatMessage(reservationMessages.upcoming),
        [RESERVATION_STATUS.ACTIVE]: formatMessage(reservationMessages.active),
        [RESERVATION_STATUS.PAST]: formatMessage(reservationMessages.past),
        [RESERVATION_STATUS.CANCELLED]: formatMessage(reservationMessages.cancelled),
    };

    return (
        <div className="flex">
            <Badge theme={badgeColor[vehiclesReservationData.status]}>
                <FontAwesomeIcon icon={badgeIcon[vehiclesReservationData.status]} />
                <div className="whitespace-nowrap">{badgeText[vehiclesReservationData.status]}</div>
            </Badge>
        </div>
    );
};

export const PaymentStatusBadgeReservationDetails = ({ paymentStatus }: { paymentStatus: PAYMENT_ORDER_STATUS }) => {
    const { formatMessage } = useIntl();

    const badgeColor: Record<PAYMENT_ORDER_STATUS | string, BadgeTheme> = {
        [PAYMENT_ORDER_STATUS.PAID]: 'green',
        [PAYMENT_ORDER_STATUS.PENDING]: 'gray',
        [PAYMENT_ORDER_STATUS.CANCELLED]: 'red',
        [PAYMENT_ORDER_STATUS.FAILED]: 'red',
        [PAYMENT_ORDER_STATUS.COMPLETED]: 'green',
        [PAYMENT_ORDER_STATUS.ROLLBACK]: 'red',
        [PAYMENT_ORDER_STATUS.REFUNDED]: 'orange',
    };

    const badgeIcon: Record<PAYMENT_ORDER_STATUS | string, IconDefinition> = {
        [PAYMENT_ORDER_STATUS.PAID]: faCheckCircle,
        [PAYMENT_ORDER_STATUS.COMPLETED]: faCheckCircle,
        [PAYMENT_ORDER_STATUS.PENDING]: faLoader,
        [PAYMENT_ORDER_STATUS.FAILED]: faXmarkCircle,
        [PAYMENT_ORDER_STATUS.CANCELLED]: faXmarkCircle,
        [PAYMENT_ORDER_STATUS.ROLLBACK]: faXmarkCircle,
        [PAYMENT_ORDER_STATUS.REFUNDED]: faCheckCircle,
    };

    const badgeText: Record<PAYMENT_ORDER_STATUS | string, string> = {
        [PAYMENT_ORDER_STATUS.PAID]: formatMessage(reservationMessages.successful),
        [PAYMENT_ORDER_STATUS.COMPLETED]: formatMessage(reservationMessages.successful),
        [PAYMENT_ORDER_STATUS.PENDING]: formatMessage(reservationMessages.pending),
        [PAYMENT_ORDER_STATUS.FAILED]: formatMessage(reservationMessages.failed),
        [PAYMENT_ORDER_STATUS.CANCELLED]: formatMessage(reservationMessages.cancelled),
        [PAYMENT_ORDER_STATUS.ROLLBACK]: formatMessage(reservationMessages.rollback),
        [PAYMENT_ORDER_STATUS.REFUNDED]: formatMessage(reservationMessages.refunded),
    };

    return (
        <div className="flex">
            <Badge theme={badgeColor[paymentStatus]}>
                <FontAwesomeIcon icon={badgeIcon[paymentStatus]} />
                <div>{badgeText[paymentStatus]}</div>
            </Badge>
        </div>
    );
};

export const ReservationTypeBadge = ({
    vehiclesReservationData,
}: {
    vehiclesReservationData?: ReservationsVehicleStayList;
}) => {
    const { formatMessage } = useIntl();
    if (!vehiclesReservationData) return;

    const rowReservationType = vehiclesReservationData.vehicle_stay_reservation_type;
    const reservationType = rowReservationType.reservation_type;
    const color = reservationType === RESERVATION_TYPE.DAILY ? 'bg-emerald-600' : 'bg-blue-500';
    const name = rowReservationType.name ? `- ${rowReservationType.name}` : '';
    return (
        <div className="flex items-center whitespace-nowrap">
            <Badge className="bg-white">
                <div className={`size-1.5 rounded-full ${color}`}></div>
                <div>{formatMessage(reservationMessages[reservationType])}</div>
            </Badge>
            <div className="ml-2">{`v${rowReservationType.version_number} ${name}`}</div>
        </div>
    );
};

export const LatestScheduleChangesBadge = ({
    reservationTypeDetails,
}: {
    reservationTypeDetails?: ReservationType;
}): ReactElement | undefined => {
    const { formatMessage } = useIntl();
    if (!reservationTypeDetails) return;

    const isLatest = reservationTypeDetails.valid_to === null;
    const title = isLatest ? reservationMessages.latestVersion : reservationMessages.scheduledChanges;
    return (
        <div className="flex">
            <Badge className="ml-2 max-w-40 flex-1 whitespace-nowrap">
                {isLatest ? (
                    <div className="size-1.5 rounded-full bg-emerald-600" />
                ) : (
                    <FontAwesomeIcon icon={faArrowUpRight} />
                )}
                <div>{formatMessage(title)}</div>
            </Badge>
        </div>
    );
};
